import { useState, useEffect } from 'react';
import { Close, Cookie, Security, Info } from '@mui/icons-material';
import {
  CookieConsentWrapper,
  CookieConsentContent,
  CookieIcon,
  CookieText,
  CookieTitle,
  CookieDescription,
  CookieButtons,
  AcceptButton,
  DeclineButton,
  SettingsButton,
  CloseButton,
  CookieModal,
  ModalContent,
  ModalHeader,
  ModalBody,
  CookieCategory,
  CategoryHeader,
  CategoryDescription,
  ToggleSwitch,
  ModalButtons
} from './styles';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // תמיד מופעל
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // בדיקה אם המשתמש כבר נתן הסכמה
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // המתנה קצרה לפני הצגת הבאנר
      setTimeout(() => setIsVisible(true), 2000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    
    // הפעלת Google Analytics אם קיים
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted'
      });
    }
  };

  const handleDeclineAll = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    
    // ביטול Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    setShowSettings(false);
    
    // עדכון Google Analytics לפי ההעדפות
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: preferences.analytics ? 'granted' : 'denied',
        ad_storage: preferences.marketing ? 'granted' : 'denied'
      });
    }
  };

  const handleTogglePreference = (category) => {
    if (category === 'necessary') return; // לא ניתן לבטל עוגיות נחוצות
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      <CookieConsentWrapper>
        <CookieConsentContent>
          <CookieIcon>
            <Cookie />
          </CookieIcon>
          
          <CookieText>
            <CookieTitle>שימוש בעוגיות באתר</CookieTitle>
            <CookieDescription>
              האתר שלנו משתמש בעוגיות (Cookies) כדי לשפר את חוויית הגלישה שלכם, לנתח תנועה באתר ולהציג תוכן רלוונטי. 
              על פי חוק הגנת הפרטיות התשמ"א-1981 וחוק התקשורת (בזק ושידורים) התשמ"ב-1982, אנו נדרשים לקבל את הסכמתכם לשימוש בעוגיות.
              <br /><br />
              <strong>עוגיות נחוצות:</strong> נדרשות לתפקוד בסיסי של האתר ואינן ניתנות לביטול.
              <br />
              <strong>עוגיות אנליטיות:</strong> עוזרות לנו להבין כיצד אתם משתמשים באתר.
              <br />
              <strong>עוגיות שיווק:</strong> משמשות להצגת פרסומות רלוונטיות.
            </CookieDescription>
          </CookieText>

          <CookieButtons>
            <AcceptButton onClick={handleAcceptAll}>
              <Security />
              אישור כל העוגיות
            </AcceptButton>
            <SettingsButton onClick={() => setShowSettings(true)}>
              <Info />
              הגדרות מתקדמות
            </SettingsButton>
            <DeclineButton onClick={handleDeclineAll}>
              דחיית עוגיות לא נחוצות
            </DeclineButton>
          </CookieButtons>

          <CloseButton onClick={handleDeclineAll}>
            <Close />
          </CloseButton>
        </CookieConsentContent>
      </CookieConsentWrapper>

      {showSettings && (
        <CookieModal>
          <ModalContent>
            <ModalHeader>
              <h3>הגדרות עוגיות מתקדמות</h3>
              <CloseButton onClick={() => setShowSettings(false)}>
                <Close />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <CookieCategory>
                <CategoryHeader>
                  <h4>עוגיות נחוצות</h4>
                  <ToggleSwitch checked disabled>
                    <span>חובה</span>
                  </ToggleSwitch>
                </CategoryHeader>
                <CategoryDescription>
                  עוגיות אלו נחוצות לתפקוד בסיסי של האתר, כולל אבטחה, ניווט ופונקציות בסיסיות. לא ניתן לבטל עוגיות אלו.
                </CategoryDescription>
              </CookieCategory>

              <CookieCategory>
                <CategoryHeader>
                  <h4>עוגיות אנליטיות</h4>
                  <ToggleSwitch 
                    checked={preferences.analytics}
                    onClick={() => handleTogglePreference('analytics')}
                  >
                    <span>{preferences.analytics ? 'מופעל' : 'מבוטל'}</span>
                  </ToggleSwitch>
                </CategoryHeader>
                <CategoryDescription>
                  עוגיות אלו עוזרות לנו להבין כיצד המבקרים משתמשים באתר, כולל Google Analytics לצורך שיפור האתר.
                </CategoryDescription>
              </CookieCategory>

              <CookieCategory>
                <CategoryHeader>
                  <h4>עוגיות שיווק</h4>
                  <ToggleSwitch 
                    checked={preferences.marketing}
                    onClick={() => handleTogglePreference('marketing')}
                  >
                    <span>{preferences.marketing ? 'מופעל' : 'מבוטל'}</span>
                  </ToggleSwitch>
                </CategoryHeader>
                <CategoryDescription>
                  עוגיות אלו משמשות להצגת פרסומות רלוונטיות ולמדידת יעילות קמפיינים פרסומיים.
                </CategoryDescription>
              </CookieCategory>

              <CookieCategory>
                <CategoryHeader>
                  <h4>עוגיות פונקציונליות</h4>
                  <ToggleSwitch 
                    checked={preferences.functional}
                    onClick={() => handleTogglePreference('functional')}
                  >
                    <span>{preferences.functional ? 'מופעל' : 'מבוטל'}</span>
                  </ToggleSwitch>
                </CategoryHeader>
                <CategoryDescription>
                  עוגיות אלו מאפשרות פונקציות משופרות כמו זכירת העדפות משתמש ושיפור חוויית הגלישה.
                </CategoryDescription>
              </CookieCategory>
            </ModalBody>

            <ModalButtons>
              <AcceptButton onClick={handleSavePreferences}>
                שמירת הגדרות
              </AcceptButton>
              <DeclineButton onClick={() => setShowSettings(false)}>
                ביטול
              </DeclineButton>
            </ModalButtons>
          </ModalContent>
        </CookieModal>
      )}
    </>
  );
};

export default CookieConsent;
